import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const ScanRedirectPage = () => {
    const { participantId } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('checking'); // checking | redirecting | profile | not-found | error
    const [info, setInfo] = useState(null);

    useEffect(() => {
        // If admin token present → go straight to check-in
        const token = localStorage.getItem('token');
        if (token) {
            navigate(`/v2/checkin/${participantId}`, { replace: true });
            return;
        }

        // Otherwise look up vcardqr profile
        fetch(`${API}/api/v2/participants/${participantId}/vcard-profile`)
            .then(r => r.json())
            .then(data => {
                if (!data.success) { setStatus('error'); return; }
                setInfo(data);
                if (data.vcardFound) {
                    // Redirect straight to the vcardqr profile
                    window.location.href = data.profileUrl;
                } else {
                    setStatus('not-found');
                }
            })
            .catch(() => setStatus('error'));
    }, [participantId, navigate]);

    // ── Loading / redirecting ────────────────────────────────────────────────
    if (status === 'checking') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-sm text-gray-500">Loading profile…</p>
                </div>
            </div>
        );
    }

    // ── Error ────────────────────────────────────────────────────────────────
    if (status === 'error') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="text-center max-w-sm">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2">Something went wrong</h2>
                    <p className="text-sm text-gray-500">Could not load this participant's profile.</p>
                </div>
            </div>
        );
    }

    // ── No vCard profile — show basic participant info ────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-sm">
                {/* Card */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    {/* Header bar */}
                    <div className="h-2 bg-gradient-to-r from-indigo-500 to-purple-500" />

                    <div className="p-8 text-center">
                        {/* Avatar placeholder */}
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center mx-auto mb-4 shadow-md">
                            <span className="text-3xl font-black text-white">
                                {info?.participantName?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                        </div>

                        <h1 className="text-xl font-bold text-gray-900 mb-1">{info?.participantName}</h1>

                        {info?.categoryName && (
                            <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full mb-3">
                                {info.categoryName}
                            </span>
                        )}

                        <div className="mt-4 space-y-2 text-sm text-gray-600">
                            <div className="flex items-center justify-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{info?.eventName}</span>
                            </div>

                            {info?.eventDate && (
                                <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
                                    <span>{info.eventDate}</span>
                                </div>
                            )}

                            {info?.registrationId && (
                                <div className="mt-3 px-4 py-2 bg-gray-50 rounded-xl border border-gray-100">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Registration ID</p>
                                    <p className="font-mono font-bold text-indigo-700 text-sm">{info.registrationId}</p>
                                </div>
                            )}
                        </div>

                        <p className="mt-6 text-xs text-gray-400">
                            This participant hasn't set up a digital profile yet.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScanRedirectPage;
